import { useEffect, useState } from "react";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import CatalogManager from "../components/CatalogManager";
import { useAuth } from "../lib/auth";
import { Search, Boxes, RefreshCcw, Wifi, Package } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Inventory() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState("catalog");
  const [inv, setInv] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    const params = branchId ? { branch_id: branchId } : {};
    api.get("/inventory", { params }).then((r) => setInv(r.data));
  };

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  useEffect(() => { if (tab === "stock") load(); }, [branchId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/wansoft/sync");
      toast.success(t("inventory.syncedToast"));
      load();
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setSyncing(false);
    }
  };

  const filtered = inv.filter((i) => !search || i.supply_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <Topbar title={t("inventory.title")} subtitle={t("inventory.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
          <button data-testid="inv-tab-catalog" onClick={() => setTab("catalog")}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${tab === "catalog" ? "bg-white text-[#1A1A1A] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Package className="w-4 h-4" /> {t("inventory.tabCatalog")}
          </button>
          <button data-testid="inv-tab-stock" onClick={() => setTab("stock")}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${tab === "stock" ? "bg-white text-[#1A1A1A] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Boxes className="w-4 h-4" /> {t("inventory.tabStock")}
          </button>
        </div>

        {tab === "catalog" && <CatalogManager canEdit={user?.role === "corporate_admin"} />}

        {tab === "stock" && (
        <>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="inv-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("inventory.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>

          {user?.role !== "branch_manager" && (
            <select value={branchId || ""} onChange={(e) => setBranchId(e.target.value || null)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
              <option value="">{t("inventory.allBranches")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}

          {user?.role === "corporate_admin" && (
            <button data-testid="inv-sync-btn" onClick={sync} disabled={syncing}
              className="px-4 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
              <RefreshCcw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? t("inventory.syncing") : t("inventory.sync")}
            </button>
          )}
        </div>

        {/* POS connection banner */}
        <div className="bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Wifi className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm text-emerald-900">{t("inventory.posConnected")}</div>
            <div className="text-xs text-emerald-700 mt-0.5">{t("inventory.posConnectedSub")}</div>
          </div>
          <span className="px-2 py-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full">LIVE</span>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={t("empty.defaultTitle")} message={t("empty.defaultMessage")} icon={Boxes} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("inventory.tableSupply")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("inventory.tableCategory")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("inventory.tableBranch")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("inventory.tableSku")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("inventory.tableStock")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const lowStock = (it.quantity_on_hand || 0) < 20;
                  return (
                    <tr key={`${it.supply_id}-${it.branch_id}`}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-6 py-3.5 text-sm font-bold text-[#1A1A1A]">{it.supply_name}</td>
                      <td className="px-6 py-3.5 text-sm text-slate-600 capitalize">{it.category}</td>
                      <td className="px-6 py-3.5 text-sm">{it.branch_name}</td>
                      <td className="px-6 py-3.5 font-mono text-xs text-slate-500">{it.pos_sku}</td>
                      <td className="px-6 py-3.5 text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-lg font-mono font-bold text-sm ${lowStock ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                          {(it.quantity_on_hand || 0).toFixed(1)} {it.unit}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>
    </>
  );
}
